// src/components/banners/BannerGenerator.jsx
import React, { useRef, useImperativeHandle, forwardRef } from 'react';
import { toPng } from 'html-to-image';
import MatchBanner from './banners/MatchBanner';
import RoundBanner from './banners/RoundBanner';
import DayBanner from './banners/DayBanner';

const BannerGenerator = forwardRef(({
  championship,
  match,
  matches = [],
  dateTitle,
  sponsors = [],
  mode = 'match',
  backgroundIndex = 0,
}, ref) => {
  const bannerRef = useRef();

  useImperativeHandle(ref, () => ({
    /**
     * Gera o banner e retorna a URL da imagem
     * @param {boolean} autoDownload - Se true, faz o download automático
     * @returns {Promise<string>} URL da imagem gerada
     */
    generateBanner: async (autoDownload = false) => {
      try {
        if (!bannerRef.current) {
          throw new Error('Referência do banner não encontrada');
        }
        
        // Aguarda um frame para garantir que o DOM foi renderizado
        await new Promise(resolve => requestAnimationFrame(resolve));
        
        const dataUrl = await toPng(bannerRef.current, {
          quality: 1,
          pixelRatio: 1,
          backgroundColor: '#000000',
          width: 1080,
          height: 1350,
        });
        
        // Se autoDownload for true, faz o download automático
        if (autoDownload) {
          const link = document.createElement('a');
          link.download = `banner-${Date.now()}.png`;
          link.href = dataUrl;
          link.click();
        }
        
        // Retorna a URL para uso em pré-visualização
        return dataUrl;
      } catch (error) {
        console.error('Erro ao gerar banner:', error);
        throw new Error('Erro ao gerar banner: ' + error.message);
      }
    },
    
    /**
     * Gera o banner e faz o download automático
     * @returns {Promise<void>}
     */
    downloadBanner: async () => {
      try {
        const dataUrl = await ref.current.generateBanner(true);
        return dataUrl;
      } catch (error) {
        console.error('Erro ao baixar banner:', error);
        alert('Erro ao baixar banner. Tente novamente.');
        throw error;
      }
    }
  }));

  return (
    <div style={{ position: 'absolute', left: -9999, top: 0, zIndex: -1 }}>
      <div ref={bannerRef}>
        {mode === 'match' && match && (
          <MatchBanner
            championship={championship}
            match={match}
            backgroundIndex={backgroundIndex}
            sponsors={sponsors}
          />
        )}
        {mode === 'round' && (
          <RoundBanner
            championship={championship}
            matches={matches}
            dateTitle={dateTitle}
            sponsors={sponsors}
            backgroundIndex={backgroundIndex}
          />
        )}
        {mode === 'day' && (
          <DayBanner
            championship={championship}
            matches={matches}
            dateTitle={dateTitle || 'JOGOS DE HOJE'}
            sponsors={sponsors}
            backgroundIndex={backgroundIndex}
          />
        )}
      </div>
    </div>
  );
});

BannerGenerator.displayName = 'BannerGenerator';

export default BannerGenerator;